import { useEffect, useCallback } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { BookingDetails } from './types';

interface UseRealtimeBookingsProps {
  bookings: BookingDetails[];
  setBookings: React.Dispatch<React.SetStateAction<BookingDetails[]>>;
  isAdmin: boolean;
  userId?: string;
}

export const useRealtimeBookings = ({
  bookings,
  setBookings,
  isAdmin,
  userId
}: UseRealtimeBookingsProps) => {
  const handleBookingUpdate = useCallback((payload: any) => {
    console.log('[REALTIME-BOOKINGS] Received booking update:', payload);
    
    const updatedBooking = payload.new as BookingDetails;
    const oldBooking = payload.old as BookingDetails;
    
    // Only process updates for bookings the user should see
    const shouldUpdate = isAdmin || updatedBooking.user_id === userId;
    
    if (!shouldUpdate) {
      console.log('[REALTIME-BOOKINGS] Skipping update - user not authorized');
      return;
    }

    setBookings(prevBookings => {
      const existingIndex = prevBookings.findIndex(b => b.id === updatedBooking.id);
      
      if (existingIndex >= 0) {
        // CRITICAL FIX: Only update specific fields to preserve joined data
        // Realtime updates don't include contacts, addresses, parcels - they come from joins
        const newBookings = [...prevBookings];
        const existingBooking = newBookings[existingIndex];
        
        // Only update the basic booking fields, preserve the complex joined data
        newBookings[existingIndex] = {
          ...existingBooking, // Keep existing contacts, addresses, parcels
          ...updatedBooking,  // Update basic fields like status
          // Explicitly preserve joined data that realtime doesn't send
          collection_contacts: existingBooking.collection_contacts,
          delivery_contacts: existingBooking.delivery_contacts,
          parcels: existingBooking.parcels,
          collection_address: existingBooking.collection_address,
          delivery_address: existingBooking.delivery_address
        };
        
        console.log('[REALTIME-BOOKINGS] Updated booking fields while preserving joined data:', updatedBooking.id);
        return newBookings;
      } else {
        // Add new booking if user should see it (this would need a full fetch anyway)
        console.log('[REALTIME-BOOKINGS] Adding new booking:', updatedBooking.id);
        return [updatedBooking, ...prevBookings];
      }
    });
  }, [setBookings, isAdmin, userId]);

  const handleBookingInsert = useCallback(async (payload: any) => {
    console.log('[REALTIME-BOOKINGS] Received booking insert:', payload);
    
    const newBooking = payload.new as BookingDetails;
    const shouldAdd = isAdmin || newBooking.user_id === userId;
    
    if (!shouldAdd) {
      console.log('[REALTIME-BOOKINGS] Skipping insert - user not authorized');
      return;
    }

    // Fetch complete booking data with all joins
    console.log('[REALTIME-BOOKINGS] Fetching complete booking data for:', newBooking.id);
    const { data: completeBooking, error } = await supabase
      .from('bookings')
      .select(`
        *,
        collection_address:address_book!bookings_collection_address_id_fkey(*),
        delivery_address:address_book!bookings_delivery_address_id_fkey(*)
      `)
      .eq('id', newBooking.id)
      .single();

    if (error) {
      console.error('[REALTIME-BOOKINGS] Error fetching complete booking:', error);
      return;
    }

    // Fetch profile separately
    const { data: profile } = await supabase
      .from('profiles')
      .select('name, phone_number, email')
      .eq('id', completeBooking.user_id)
      .maybeSingle();

    // Fetch contacts via booking_contacts junction table
    const { data: contactRelations } = await supabase
      .from('booking_contacts')
      .select(`
        contact_type,
        is_primary,
        sort_order,
        contact_id,
        contact_book(*)
      `)
      .eq('booking_id', newBooking.id)
      .order('sort_order', { ascending: true });

    // Fetch parcels
    const { data: parcelsData } = await supabase
      .from('booking_parcels')
      .select('*')
      .eq('booking_id', newBooking.id);

    // Transform into BookingDetails format
    const collectionContacts = contactRelations
      ?.filter(cr => cr.contact_type === 'collection')
      .map(cr => ({
        name: cr.contact_book?.name || '',
        phone: cr.contact_book?.phone || '',
        email: cr.contact_book?.email || undefined
      })) || [];

    const deliveryContacts = contactRelations
      ?.filter(cr => cr.contact_type === 'delivery')
      .map(cr => ({
        name: cr.contact_book?.name || '',
        phone: cr.contact_book?.phone || '',
        email: cr.contact_book?.email || undefined
      })) || [];

    // Transform parcels with proper typing
    const parcels = parcelsData?.map(p => ({
      id: p.id,
      parcel_type: p.parcel_type,
      description: p.description,
      weight: p.weight,
      dimensions: p.dimensions as { length: string; width: string; height: string; } | null,
      value: p.value,
      quantity: p.quantity,
      other_type_specification: p.other_type_specification,
      insurance_enabled: p.insurance_enabled
    })) || [];

    const fullBooking: BookingDetails = {
      id: completeBooking.id,
      status: completeBooking.status,
      created_at: completeBooking.created_at,
      collection_datetime: completeBooking.collection_datetime,
      user_id: completeBooking.user_id,
      org_id: completeBooking.org_id,
      total_parcels_count: completeBooking.total_parcels_count,
      collection_address: completeBooking.collection_address,
      delivery_address: completeBooking.delivery_address,
      collection_contacts: collectionContacts,
      delivery_contacts: deliveryContacts,
      parcels: parcels,
      payment_url: null,
      tracking_url: completeBooking.tracking_url,
      payment_confirmed: null,
      payment_confirmed_by: null,
      payment_confirmed_at: null,
      fulfillment_details: completeBooking.fulfillment_details as any,
      profiles: profile || undefined
    };

    setBookings(prevBookings => {
      // Check if booking already exists to avoid duplicates
      const exists = prevBookings.some(b => b.id === fullBooking.id);
      if (!exists) {
        console.log('[REALTIME-BOOKINGS] Adding complete booking:', fullBooking.id);
        return [fullBooking, ...prevBookings];
      }
      return prevBookings;
    });
  }, [setBookings, isAdmin, userId]);

  const handleBookingDelete = useCallback((payload: any) => {
    console.log('[REALTIME-BOOKINGS] Received booking delete:', payload);
    
    const deletedBooking = payload.old as BookingDetails;
    
    setBookings(prevBookings => {
      const filtered = prevBookings.filter(b => b.id !== deletedBooking.id);
      console.log('[REALTIME-BOOKINGS] Removed booking:', deletedBooking.id);
      return filtered;
    });
  }, [setBookings]);

  useEffect(() => {
    console.log('[REALTIME-BOOKINGS] Setting up realtime subscription');
    
    let channel: RealtimeChannel;
    
    const setupRealtimeSubscription = async () => {
      // Create channel for booking updates
      channel = supabase
        .channel('booking-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bookings'
          },
          handleBookingUpdate
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'bookings'
          },
          handleBookingInsert
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'bookings'
          },
          handleBookingDelete
        )
        .subscribe((status) => {
          console.log('[REALTIME-BOOKINGS] Subscription status:', status);
        });
    };

    setupRealtimeSubscription();

    return () => {
      console.log('[REALTIME-BOOKINGS] Cleaning up realtime subscription');
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [handleBookingUpdate, handleBookingInsert, handleBookingDelete]);

  return {
    // Expose methods for manual refresh if needed
    refreshBookings: () => {
      console.log('[REALTIME-BOOKINGS] Manual refresh requested');
    }
  };
};